import { Fragment } from 'react';

type StepKey = 'draft' | 'sent' | 'fulfilled' | 'received' | 'completed';

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'draft', label: 'Draft' },
  { key: 'sent', label: 'Sent' },
  { key: 'fulfilled', label: 'Fulfilled' },
  { key: 'received', label: 'Received' },
  { key: 'completed', label: 'Completed' },
];

const STEP_INDEX: Record<string, number> = {
  draft: 0,
  sent: 1,
  fulfilled: 2,
  received: 3,
  completed: 4,
};

interface Props {
  status: string;
  sourceType?: string | null;
}

// Compact horizontal progress indicator for the PO list. Replaces the legacy
// Status badge + Flags column combo by visualizing where in the lifecycle a PO
// sits: Draft → Sent → [Fulfilled] → Received → Completed. Fulfilled is
// rendered as a muted/skipped step for third-party POs (which never enter
// that state). Cancelled short-circuits the stepper to a single red pill.
export function PoStatusStepper({ status, sourceType }: Props) {
  if (status === 'cancelled') {
    return (
      <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
        Cancelled
      </span>
    );
  }

  const currentIdx = STEP_INDEX[status] ?? 0;
  const isWarehouse = sourceType === 'warehouse';

  return (
    <div className="inline-flex items-start">
      {STEPS.map((step, idx) => {
        const isFulfilledSkipped = step.key === 'fulfilled' && !isWarehouse;
        const reached = !isFulfilledSkipped && idx <= currentIdx;
        const nextReached = idx < currentIdx;
        return (
          <Fragment key={step.key}>
            <div className="flex w-14 flex-col items-center">
              <div
                className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border ${
                  reached
                    ? 'border-green-600 bg-green-600'
                    : isFulfilledSkipped
                      ? 'border-dashed border-muted-foreground/40 bg-white'
                      : 'border-muted-foreground/40 bg-white'
                }`}
              >
                {reached && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
              </div>
              <span
                className={`mt-1 text-[10px] leading-tight ${
                  reached
                    ? 'font-medium text-foreground'
                    : isFulfilledSkipped
                      ? 'italic text-muted-foreground/60'
                      : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`mt-[6px] h-0.5 w-3 ${nextReached ? 'bg-green-600' : 'bg-muted'}`} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
